import { useRef, useState } from "react";

/**
 * Dragging rows into a new order.
 *
 * Pointer events rather than the native HTML5 drag-and-drop API, and the reason
 * is worth writing down because HTML5 DnD looks like the obvious choice:
 *
 *   - It does not fire from touch at all without a polyfill, so on a phone or a
 *     tablet the handles would simply do nothing.
 *   - It cannot be driven by synthetic mouse events, which means it cannot be
 *     exercised in a browser test — a reorder control that silently stops
 *     saving is exactly the kind of thing that should not rely on somebody
 *     noticing by hand.
 *
 * Pointer events give one code path for mouse, touch and pen, and
 * `setPointerCapture` keeps the drag alive when the cursor leaves the row.
 *
 * It is deliberately not a general drag system: no cross-list dropping, no
 * nesting, no animation. None of the places this is used wants any of them.
 *
 * The caller is handed indices and decides what a move means. Positions here are
 * stored per row and saved one PATCH at a time, so the hook cannot know how to
 * persist — and the one thing worse than no drag-and-drop is a drag that appears
 * to work and silently does not save.
 */
export interface ReorderOptions {
  /** False while somebody may not reorder -- a non-admin, a read-only list. */
  enabled?: boolean;
  /**
   * Which way the list runs.
   *
   * Rows stack down the page; a table's headings run across it. Comparing the
   * wrong coordinate does not fail loudly -- every drop simply lands on the row
   * the pointer started on, because nothing else ever contains it.
   */
  axis?: "x" | "y";
  /**
   * How far the pointer must travel before this counts as a drag, in pixels.
   *
   * Zero for a dedicated grip, which has nothing to do but drag. Above zero
   * when the thing being dragged is also a button: a table heading sorts when
   * it is clicked, and a drag that began the moment the pointer went down
   * would swallow that click and leave the column unsortable.
   */
  threshold?: number;
}

export interface ReorderHandlers {
  /** Spread onto each row, so a pointer can be located over it. */
  row: (index: number) => {
    "data-reorder-index": number;
    ref: (node: HTMLElement | null) => void;
  };
  /** Spread onto the drag handle inside each row. */
  handle: (index: number) => {
    onPointerDown: (event: React.PointerEvent) => void;
    style: { touchAction: "none" };
  };
  /** The row being carried, for styling. */
  dragging: number | null;
  /** The row it is currently over, for the drop indicator. */
  over: number | null;
}

export function useReorder(
  onMove: (from: number, to: number) => void,
  { enabled = true, axis = "y", threshold = 0 }: ReorderOptions = {},
): ReorderHandlers {
  const [dragging, setDragging] = useState<number | null>(null);
  const [over, setOver] = useState<number | null>(null);

  /*
   * Live values for the pointer handlers. Refs rather than state because the
   * move handler is created once per drag and would otherwise close over the
   * state as it was when the drag started.
   */
  const rows = useRef(new Map<number, HTMLElement>());
  const from = useRef<number | null>(null);
  const target = useRef<number | null>(null);

  /** One row's extent along whichever axis the list runs. */
  const span = (node: HTMLElement): { start: number; end: number } => {
    const box = node.getBoundingClientRect();
    return axis === "x"
      ? { start: box.left, end: box.right }
      : { start: box.top, end: box.bottom };
  };

  /** Where the pointer is along that same axis. */
  const along = (event: { clientX: number; clientY: number }): number =>
    axis === "x" ? event.clientX : event.clientY;

  /**
   * Which row the pointer is over, by comparing against each row's midpoint.
   *
   * Measured on every move rather than cached at drag start: these lists sit
   * inside panels that can scroll, and a cached rectangle is a drop that lands
   * one row out after somebody scrolls.
   */
  const rowAt = (position: number): number | null => {
    let closest: number | null = null;
    for (const [index, node] of rows.current) {
      const box = span(node);
      if (position >= box.start && position <= box.end) return index;
      /* Past the end of the list in either direction still has an answer, so
         dropping slightly above the first row means "first". */
      if (closest === null) closest = index;
      else {
        const other = span(rows.current.get(closest)!);
        const here = Math.abs(position - (box.start + box.end) / 2);
        const there = Math.abs(position - (other.start + other.end) / 2);
        if (here < there) closest = index;
      }
    }
    return closest;
  };

  return {
    dragging,
    over,

    row: (index) => ({
      "data-reorder-index": index,
      ref: (node) => {
        if (node) rows.current.set(index, node);
        else rows.current.delete(index);
      },
    }),

    handle: (index) => ({
      style: { touchAction: "none" },
      onPointerDown: (event) => {
        if (!enabled) return;
        // Left button or a touch. A right-click should open a menu, not drag.
        if (event.button !== 0) return;

        const node = event.currentTarget as HTMLElement;
        const pointer = event.pointerId;
        const origin = along(event);
        let started = false;

        /**
         * Take hold, either at once or once the pointer has travelled far
         * enough.
         *
         * Capture happens here rather than on the way in, because capturing a
         * pointer that then turns out to be a click would redirect the events
         * that make up that click away from the button underneath.
         */
        const begin = (): void => {
          started = true;
          from.current = index;
          target.current = index;
          setDragging(index);
          setOver(index);
          node.setPointerCapture(pointer);
        };

        /* No threshold means a dedicated grip: nothing underneath wants the
           click, so preventing it here stops the text selection a drag across
           a list would otherwise paint. */
        if (threshold === 0) {
          event.preventDefault();
          begin();
        }

        const onMoveEvent = (move: PointerEvent): void => {
          if (move.pointerId !== pointer) return;
          if (!started) {
            if (Math.abs(along(move) - origin) < threshold) return;
            begin();
          }

          /* Once it is a drag it is not a selection. Said on every move
             because the one on pointerdown was deliberately not sent. */
          move.preventDefault();

          const at = rowAt(along(move));
          if (at !== null && at !== target.current) {
            target.current = at;
            setOver(at);
          }
        };

        const onUp = (release: PointerEvent): void => {
          if (release.pointerId !== pointer) return;
          window.removeEventListener("pointermove", onMoveEvent);
          window.removeEventListener("pointerup", onUp);
          window.removeEventListener("pointercancel", onUp);
          /* Nothing was ever taken hold of if the pointer did not travel, and
             releasing a capture that was never made throws. */
          if (!started) return;

          if (node.hasPointerCapture(pointer)) node.releasePointerCapture(pointer);

          const start = from.current;
          const end = target.current;
          from.current = null;
          target.current = null;
          setDragging(null);
          setOver(null);

          if (start !== null && end !== null && start !== end)
            onMove(start, end);
        };

        /*
         * On the window, not on the node, and that is the whole reason a
         * threshold drag works at all.
         *
         * Capture is what normally keeps a drag's events coming back to the
         * thing they started on -- but with a threshold there is nothing to
         * capture with until the pointer has already travelled. Listening on
         * the node meant the first move had to land inside it: fine for a tall
         * row, useless for a table heading two centimetres wide, where a brisk
         * sideways flick puts the very first move over a different column and
         * the drag never begins. The window sees every move, and the pointer
         * id is checked because it sees other people's too.
         */
        window.addEventListener("pointermove", onMoveEvent);
        window.addEventListener("pointerup", onUp);
        window.addEventListener("pointercancel", onUp);
      },
    }),
  };
}

/**
 * The list `items` becomes when the row at `from` is dropped on `to`.
 *
 * Separate from the hook so callers can diff it against what they had and save
 * only the rows that actually moved — which on a five-row list is usually two.
 */
export function moved<T>(items: readonly T[], from: number, to: number): T[] {
  const next = [...items];
  const [carried] = next.splice(from, 1);
  if (carried === undefined) return next;
  next.splice(to, 0, carried);
  return next;
}
